import { ref } from 'vue';
import { z } from 'zod';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import { useInternational } from '@/common/locale';
import { useMessage } from '@/common/inject/useMessage';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { getSlideDetailStore, TOTAL_DETAIL } from '@/common/stores/slide-detail';
import { useTargetPermissionCheck } from '@/common/permission/permission.utils';
import { TargetStatusRequestTargetKind } from '@/api-client/target/model';
import type { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import type {
  SlideDetailAdapterParams,
  SlideDetailAdapterResult,
  SlideDetailAdapterResultKey,
} from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';
import type { WebServerTypeWithNone } from '@/application/utils/types';
import { getV8ApplicationWsmInfo } from '@/api-client/wsm/queries/application-wsm-controller/application-wsm-controller';

const webServerDetailInputSchema = z
  .object({
    wsmId: z.string().optional(),
    type: z.string().optional(),
    targetId: z.string().optional(),
    targetName: z.string().optional(),
    rawData: z.record(z.any()).optional(),
    fromTime: z.string().optional(),
    toTime: z.string().optional(),
  })
  .transform((input) => {
    const wsmId = input.wsmId || input.rawData?.wsmId || input.targetId || '';
    return {
      ...input,
      targetId: input.targetId ?? wsmId,
      wsmId,
    };
  })
  .pipe(
    z.object({
      wsmId: z.string().min(1),
      type: z.string().optional(),
      targetId: z.string().optional(),
      targetName: z.string().optional(),
      rawData: z.record(z.any()).optional(),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export type WebServerDetailInput = z.input<typeof webServerDetailInputSchema>;

export const webServerDetailRequiredMap = getRequiredMap(webServerDetailInputSchema);

const toWebServerTypeWithNone = (type?: string): WebServerTypeWithNone => {
  if (type === 'nginx' || type === 'apache') return type;
  return 'none';
};

const isWebServerTypeWithNone = (type?: string): type is WebServerTypeWithNone =>
  type === 'nginx' || type === 'apache' || type === 'none';

const webServerDetailFinalSchema = z.object({
  wsmId: z.string().min(1),
  type: z.union([z.literal('nginx'), z.literal('apache'), z.literal('none')]),
  targetName: z.string().optional(),
  fromTime: z.string().optional(),
  toTime: z.string().optional(),
});

const getMissingParams = (error: z.ZodError) =>
  error.issues
    .map((issue) => issue.path[0])
    .filter((path): path is string => typeof path === 'string')
    .join(', ');

// wasName과 type이 없을 경우 대상 정보 조회
const fetchWebServerInfo = async ({
  wsmId,
  fromTime,
  toTime,
}: {
  wsmId: string;
  fromTime?: string;
  toTime?: string;
}) => {
  try {
    const { data } = await getV8ApplicationWsmInfo({ wsmId, fromTime, toTime });
    const info = data?.data;
    return {
      wsmId,
      type: toWebServerTypeWithNone(info?.type),
      targetName: info?.wsmAliasName || info?.wsmName || '',
    };
  } catch {
    return {
      wsmId: wsmId || '',
      type: 'none' as const,
      targetName: '',
    };
  }
};

const resolveWebServerInfo = async (
  params: z.output<typeof webServerDetailInputSchema>,
): Promise<{
  wsmId: string;
  type: WebServerTypeWithNone;
  targetName?: string;
  fromTime?: string;
  toTime?: string;
}> => {
  if (isWebServerTypeWithNone(params.type)) {
    return {
      wsmId: params.wsmId,
      type: params.type,
      targetName: params.targetName,
      fromTime: params.fromTime,
      toTime: params.toTime,
    };
  }

  const webServerInfo = await fetchWebServerInfo({
    wsmId: params.wsmId,
    fromTime: params.fromTime,
    toTime: params.toTime,
  });
  return {
    wsmId: webServerInfo.wsmId || params.wsmId,
    type: webServerInfo.type,
    targetName: params.targetName || webServerInfo.targetName,
    fromTime: params.fromTime,
    toTime: params.toTime,
  };
};

export const useWebServerDetailAdapter = (rolePermissionKey?: RolePermissionKeyType) => {
  const isSlideDetailOpened = ref(false);
  const { showErrorMsg } = useMessage();
  const { t } = useInternational();
  const { addTabPanel, addSplitTabPanel, isDetailSlidePermissionDenied } = getSlideDetailStore();
  const { checkIsValidTarget } = useTargetPermissionCheck();

  const validateWebServerDetail = async (
    params: Partial<WebServerDetailInput>,
    options: {
      showErrorMessage: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
      checkTarget?: boolean;
    },
  ): Promise<{
    reason: SlideDetailAdapterResultKey;
    parsedParams?: z.infer<typeof webServerDetailFinalSchema> & { deleted?: boolean };
  }> => {
    const { checkPermission = true, checkParams = true, checkTarget = true } = options;

    // 0. 입력 파싱 및 필수값 검증
    const inputParsed = webServerDetailInputSchema.safeParse(params);
    if (!inputParsed.success) {
      const errorMsg = t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', {
        value: getMissingParams(inputParsed.error) || 'wsmId',
      });
      if (options.showErrorMessage) showErrorMsg({ msgStr: errorMsg });
      return { reason: 'invalid_params' };
    }
    const normalizedParams = inputParsed.data;

    let isDeletedTarget = false;

    // 1. 권한 체크
    if (checkPermission) {
      const denied = isDetailSlidePermissionDenied({
        type: TOTAL_DETAIL.WEB_SERVER,
        rolePermissionKey:
          rolePermissionKey || ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_WEB_SERVER,
        showError: options.showErrorMessage,
      });
      if (denied) return { reason: 'permission_denied' };
    }

    // 2. 타겟 유효성 검증
    if (checkTarget) {
      const targetValidation = await checkIsValidTarget(
        {
          targetId: normalizedParams.wsmId,
          targetKind: TargetStatusRequestTargetKind.WEBSERVER,
        },
        {
          showMessageIfTargetNil: options.showErrorMessage,
          showErrorMessage: false,
          isValidateDeletedTarget: false,
        },
      );
      if (targetValidation.reason === 'deleted') {
        isDeletedTarget = true;
      } else if (!targetValidation.valid) {
        if (options.showErrorMessage) {
          showErrorMsg({
            msgStr:
              targetValidation.reason === 'invalid_params'
                ? t('MESSAGE.INVALID_TARGET')
                : t('NOTI.PRODUCT.TEMP_SERVER_ERROR'),
          });
        }
        return { reason: targetValidation.reason };
      }
    }

    // 3. 대상 정보 조회 및 필수값 검증
    if (checkParams) {
      const resolvedInfo = await resolveWebServerInfo(normalizedParams);

      const parsed = webServerDetailFinalSchema.safeParse(resolvedInfo);
      if (!parsed.success) {
        const errorMsg = t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', {
          value: getMissingParams(parsed.error),
        });
        if (options.showErrorMessage) showErrorMsg({ msgStr: errorMsg });
        return { reason: 'invalid_params' };
      }
      return {
        reason: 'ok',
        parsedParams: {
          ...parsed.data,
          deleted: isDeletedTarget,
        },
      };
    }

    return { reason: 'ok' };
  };

  const openWebServerDetail = async ({
    params,
    options,
    selectedTab,
    state,
    currentState,
    init,
  }: SlideDetailAdapterParams<
    Partial<WebServerDetailInput>
  >): Promise<SlideDetailAdapterResult> => {
    const {
      runCheckPermission = true,
      showErrorMessage = true,
      runTargetValidation = true,
      openMode = 'main',
      keepSplitTab = false,
    } = options ?? {};

    const { reason, parsedParams } = await validateWebServerDetail(params, {
      showErrorMessage,
      checkPermission: runCheckPermission,
      checkTarget: runTargetValidation,
    });

    if (!parsedParams || reason !== 'ok') {
      return createSlideDetailResult(reason);
    }

    const validResult = parsedParams;

    // 4. 탭 패널 열기
    const tabPanelInfo = {
      type: TOTAL_DETAIL.WEB_SERVER,
      name: validResult.targetName ?? '',
      webServer: {
        wsmId: validResult.wsmId,
        type: validResult.type,
        deleted: validResult.deleted,
        fromTime: validResult.fromTime,
        toTime: validResult.toTime,
      },
      init,
      state: { ...state, selectedTab },
      keepSplitTab: openMode === 'split' ? keepSplitTab : undefined,
    } as const;

    if (openMode === 'split') {
      addSplitTabPanel(tabPanelInfo);
      isSlideDetailOpened.value = true;
      return createSlideDetailResult('ok');
    }

    const result = addTabPanel(tabPanelInfo, currentState);
    isSlideDetailOpened.value = getSlideDetailStore().isOpened;
    return createSlideDetailResult(result);
  };

  return { isSlideDetailOpened, openWebServerDetail };
};
